// Operator-readable copy for the three gap vocabularies the API returns: the
// per-kind render gaps (`render-request.ts`), the per-kind brand gap
// (`brand-policy.ts`), and the simple-level target gaps (`target.ts`). One table so
// every product-visual surface phrases a reason the same way; an unmapped code
// degrades to the raw reason rather than hiding it.
const GAP_MESSAGE: Record<string, string> = {
  content_not_filled: "This visual kind has no canonical content for this simple yet.",
  content_invalid: "The persisted content does not satisfy this kind's render contract.",
  etiquette_fields_incomplete: "The attributes composite is missing a required field.",
  // The old copy promised "the previous image stays published meanwhile". It never
  // did: a refusal retires that image to `outdated`, because a nutrition table
  // that no longer matches the content is a wrong image (#308). The claim below is
  // load-bearing on the read too — `config-visuals.ts` shows this cell instead of a
  // retired pointer, so the operator never sees the stale image alongside this line.
  content_exceeds_canvas:
    "The content is complete but too long to draw legibly at this kind's size. No image is published while that holds — shorten it and the next plan renders it.",
  font_coverage:
    "The content uses characters this kind's declared typeface cannot draw, so no image was rendered.",
  multilingual_identity_missing:
    "The multilingual grid needs one valid shared EAN across all six country records.",
  multilingual_identity_conflict:
    "The six country records do not prove one shared product identity.",
  multilingual_semantic_conflict:
    "A country grid repeats one nutrient meaning. Review the named language and semantic key.",
  multilingual_numeric_conflict:
    "A sibling country disagrees with Italy's numeric grid. Review the named countries before rendering.",
  multilingual_catalogue_coverage_missing:
    "A required country grid, nutrient row, or flavor is missing from the six catalogue records.",
  product_not_found: "This simple is not in the catalog mirror.",
  product_not_live: "This simple is not live and eligible.",
  // Simple-level, unlike `brand_not_allowed` below: product visuals cover the
  // nutrition master category only (#337 widened brand, not category). This line
  // names the PRODUCT, so it may only be shown when the mirror actually has a
  // category and it is out of scope — an unmapped one is
  // `catalog_missing_master_category` below.
  master_category_not_renderable:
    "Product visuals cover nutrition products only; this simple's master category is not one.",
  // The refusal above with the OTHER cause and the other remedy: no category at all in
  // the mirror. Nothing is wrong with the product, and nothing about it will fix this.
  // The remedy is named in the measured order: of the ~450 live rows fleet-wide on this
  // branch (26–28 per store, measured read-only on the shared DB 2026-08-13), 416 carry a
  // real `category_path` — only 5 distinct breadcrumbs — which per `docs/DECISIONS.md`
  // means a sync pass already reached them and the missing piece is a
  // `category_path_master_map` entry, the one `warnUnmappedCategoryBreadcrumbs` logs at
  // sync time. Only ~34 (`category_path IS NULL`) are rows no pass has reached, so the
  // resync is the second clause, not the first. Same word the backfill and `plan` report
  // as `readinessProblem`.
  catalog_missing_master_category:
    "The catalog mirror has no master category for this simple, so nothing is in render scope yet. Its category breadcrumb is most likely missing from the master-category map — add it and re-sync the store; a product no sync has reached yet is mapped by the sync itself. The product itself is fine.",
  // Per VISUAL KIND since issue #337, not per product: the nutrition image renders
  // every brand, while the other kinds stay own-brand. So this line describes the
  // cell it sits in, never the simple — the same product's nutrition cell beside it
  // can be renderable.
  brand_not_allowed: "This visual kind renders own-brand products only.",
  // Issue #304. PIM had no table, so the grid would have come from THIS store's own
  // description — and this store shares its language with another that owns that
  // fallback, so the fill wrote nothing and, deliberately, deleted nothing. Phrased
  // around the owner rather than around the failure: the operator's next move is to
  // fill from the owning store, not to retry here. The API's `detail` names that
  // store; this line is the copy shown when no detail rides along, which is every
  // read of the config-scoped gallery.
  shared_locale_grid_fallback_not_owned:
    "Another store owns this language's shared grid, so this store may not write one from its own product description. Fill it from the owning store and this cell reads it.",
  magento_id_missing: "The mirror has no Magento id for this simple.",
  name_missing: "The mirror has no product name for this simple.",
  // Client-side only: a render that settled without a JPEG and without a reason
  // of its own (`use-product-visual-preview.ts`).
  render_produced_no_image: "The render finished without producing an image. Try again.",
}

export function productVisualGapMessage(reason: string): string {
  return GAP_MESSAGE[reason] ?? `Gap: ${reason}.`
}
